/**
 * Validation telemetry — best-effort fire-and-forget reporter.
 *
 * Wired into ``useFormUX``: whenever a submit attempt fails client-side
 * validation, we POST a row to ``/api/audit/validation`` so the admin
 * panel can later see which fields atascan real users.
 *
 * Privacy: we send field NAMES + i18n message text (Zod-localized), but
 * never the user's actual values. The backend further drops messages
 * before persisting, keeping only the field-name array.
 *
 * Resilience:
 *   • Fully fire-and-forget — never throws, never blocks the form.
 *   • Per-form dedup so a frustrated user clicking Submit 10 times
 *     produces ONE row (per session-bucket) instead of ten.
 *   • Server-side per-hour cap returns 204 to silently absorb overflows.
 */
import { api } from "./api";

const SESSION_BUCKETS = new Set();

/** Dedup key — same form + same set of failing fields in one session. */
function _bucketKey(formId, fieldErrors) {
  const sorted = Object.keys(fieldErrors || {}).sort().join("|");
  return `${formId}::${sorted}`;
}

/**
 * Report a client-side validation failure. The call resolves immediately
 * (we kick the request off without awaiting). Catches all errors so the
 * caller never has to wrap in try/catch.
 *
 * @param {string} formId  caller-supplied form identifier
 * @param {Record<string,string>} fieldErrors  { field: message } map
 * @param {object} [opts]
 * @param {string} [opts.route]  current location.pathname (defaults to window.location.pathname)
 * @param {string} [opts.lang]   active language code (es/en/pt)
 */
export function reportValidationError(formId, fieldErrors, opts = {}) {
  if (!formId || !fieldErrors || Object.keys(fieldErrors).length === 0) return;
  const key = _bucketKey(formId, fieldErrors);
  if (SESSION_BUCKETS.has(key)) return;
  SESSION_BUCKETS.add(key);
  // Cap memory growth — at ~1KB per entry, 200 entries is ~200KB which
  // is far below SPA noise. If we hit that, just clear and re-start.
  if (SESSION_BUCKETS.size > 200) SESSION_BUCKETS.clear();

  const route = opts.route || (typeof window !== "undefined" ? window.location?.pathname : "") || "";
  const lang = opts.lang || (typeof localStorage !== "undefined" ? localStorage.getItem("lumfi_lang") : "") || "";
  try {
    api.post("/audit/validation", {
      form_id: formId,
      route,
      field_errors: fieldErrors,
      lang,
    }).catch(() => { /* swallow — telemetry must never bubble */ });
  } catch { /* never throw from a telemetry helper */ }
}
